import { useContext } from 'react';

import { useGetLoggedInUser } from '@revelio/auth';
import { getCurrencySymbol } from '@revelio/replots';

import { UserPreferenceKey } from '../types';
import { UserPreferencesContext } from '../userPreferenceContext';

export interface CurrencyPreference {
  exchangeRate: number;
  currencySymbol: string;
  code: string;
}

/**
 * Returns preferred currency taking precedence from global user preferences and
 * falling back to the profile currency_data.
 * @returns {number} exchangeRate - The exchange rate for the user's currency (defaults to 1)
 * @returns {string} currencySymbol - The symbol representation of the currency (e.g., $, €, £), defaults to $
 * @returns {string} code - The currency code (e.g., USD, EUR, GBP), defaults to USD
 */
export const useCurrencyPreference = (): CurrencyPreference => {
  const { preferences } = useContext(UserPreferencesContext);
  const pref = preferences?.[UserPreferenceKey.Currency];

  // Fallback to profile currency if no preference is set
  const { currency: profileCurrency } = useGetLoggedInUser();

  const code = pref?.code || profileCurrency?.code || 'USD';
  const exchangeRate = pref?.rate ?? profileCurrency?.rate ?? 1;
  const currencySymbol = getCurrencySymbol(code);

  return { exchangeRate, currencySymbol, code };
};
